import { PermissionType } from 'Services/permissions/interfaces';
import { permissionDict as p } from 'Services/permissions/permission-dict';

/**
 * Who may edit a product's translations, mirroring what translationservice
 * actually enforces — see the `@RequirePermissions` decorators on
 * `TranslationController`:
 *
 *   GET  /product/:slug/translations   translations.view    (loads the editor)
 *   POST /product/:slug/translations   translations.create  (saves it)
 *
 * Both are needed, hence entries holding two permissions each: the inner array
 * is AND-ed. A held `translations.*`, `translation-management.*` or `*.*`
 * satisfies both halves through the wildcard handling in
 * `hasRequiredPermissions`, so wildcard holders need no entry of their own.
 *
 * `update` is accepted alongside `create` on purpose. Whether the save endpoint
 * really turns an `update` holder away is decided by policy-decision-service —
 * a separate Go service whose matching rules are not readable from here — and
 * the XLSX upload, which writes through the very same handler, has been gated
 * on `update` all along without complaint. Accepting either write permission
 * keeps whoever can upload a sheet able to edit inline too, rather than betting
 * on semantics we cannot see.
 *
 * `view` alone was what let a read-only user into the editor — not because
 * `view` is wrong here, it is required to load the page, but because it was
 * accepted on its own, with no write permission beside it. Every entry below
 * pairs it with one, which is what closes that hole.
 *
 * Product permissions (`eis.products.*`) are deliberately absent: the endpoints
 * above do not accept them, so granting them here would only produce a form
 * that 403s on load. Letting product admins translate is a change to those
 * decorators, not to this list.
 *
 * Kept in one place because it is checked three times over: on the route, on
 * the menu item that opens the editor, and inside the editor itself. Those
 * lists had already drifted apart once, which is what let a view-only user in.
 */
export const TRANSLATION_EDIT_PERMISSIONS: PermissionType[][] = [
  [
    p.translation_management_translations_view,
    p.translation_management_translations_create,
  ],
  [
    p.translation_management_translations_view,
    p.translation_management_translations_update,
  ],
];
